import {
  DataGrid,
  DateBox,
  Form,
  Popover,
  Popup,
  RadioGroup,
  ScrollView,
  SelectBox,
  TextArea,
} from "devextreme-react";
import { GroupItem, Item } from "devextreme-react/form";
import { ToolbarItem } from "devextreme-react/popup";
import Validator, { RequiredRule } from "devextreme-react/validator";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { canEditAllReportProjects } from "../access";
import styled from "styled-components";
import { handleCellStyle, toastMsg } from "../utils";
import {
  Button,
  Column,
  Editing,
  Lookup,
  Toolbar,
  Item as DataGridItem,
  RequiredRule as GridRequiredRule,
} from "devextreme-react/data-grid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleInfo } from "@fortawesome/free-solid-svg-icons";
import { issueFormData } from "../data";
import { alert } from "devextreme/ui/dialog";
import axios from "axios";
import DataSource from "devextreme/data/data_source";

const FormWrapper = styled.div`
  margin: 0 20px;
  .dx-field-item-required-mark {
    color: red;
  }
  .dx-datebox-calendar .dx-dropdowneditor-icon::before {
    content: "\f026";
  }
  .dx-cell-modified:not(.dx-datagrid-invalid) {
    background-color: transparent !important;
  }
  .dx-data-row {
    white-space: pre-wrap;
  }
  /* .dx-dropdowneditor-input-wrapper .dx-texteditor-input {
    word-wrap: break-word;
    white-space: normal;
  } */
  .dx-button {
    margin: 0;
  }
`;

const LabelEditorWrapper = styled.div`
  display: flex;
  > div:first-child {
    min-width: 140px;
    margin: 4px;
  }
  > div:last-child {
    width: 100%;
  }
`;

const IssueFormPopup = ({
  contrNo,
  formData,
  handleHidePopup,
  isNewRecord,
  isPopupVisible,
  issueDataGridRef,
  projectListRef,
  yearMonth,
}) => {
  const [assocdIdDataSource, setAssocdIdDataSource] = useState([]);
  const [issueGradeDataSource, setIssueGradeDataSource] = useState(null);
  const [issueRootCauseDataSource, setIssueRootCauseDataSource] =
    useState(null);
  const [issueStatusDataSource, setIssueStatusDataSource] = useState(null);
  const [issueTypeDataSource, setIssueTypeDataSource] = useState(null);
  const [participantsDataSource, setParticipantsDataSource] = useState(null);
  const [participants, setParticipants] = useState(null);
  const userInfo = useSelector((state) => state.userInfo);
  const canEditAllProjects = canEditAllReportProjects(userInfo);
  const enteredByDataSource = useMemo(() => {
    if (!participants) return null;
    const candidates = [...participants];
    if (
      canEditAllProjects && userInfo.employeeNo &&
      !candidates.some((person) => person.employeeNo === userInfo.employeeNo)
    ) {
      candidates.push({
        employeeNo: userInfo.employeeNo,
        name: userInfo.name,
        role: userInfo.jd || userInfo.div,
      });
    }
    return new DataSource({
      store: { type: "array", data: candidates, key: "employeeNo" },
      group: "role",
    });
  }, [participants, canEditAllProjects, userInfo.employeeNo, userInfo.name, userInfo.jd, userInfo.div]);

  const cuzNAcGridRef = useRef(null);
  const issueFormRef = useRef(null);

  const id = formData?.id;
  const [year, month] = yearMonth.split("-").map(Number);

  useEffect(() => {
    let active = true;
    setParticipants(null);
    setParticipantsDataSource(null);
    setAssocdIdDataSource([]);
    const load = (url, receive, config) => {
      axios.get(url, config)
        .then((res) => { if (active) receive(res.data); })
        .catch(() => {
          if (active) toastMsg(`에러가 발생했습니다. 관리자에게 문의해주세요.`, false);
        });
    };
    const base = process.env.REACT_APP_BECCO_API_URL;
    load(`${base}/api/mthlyReport/proj/iss/assocdId/${contrNo}`, data => setAssocdIdDataSource(data.assocdId));
    load(`${base}/api/common/codeList?name=Issue Status&cat=12`, setIssueStatusDataSource);
    load(`${base}/api/common/codeList?name=Issue Grade&cat=12`, setIssueGradeDataSource);
    load(`${base}/api/common/codeList?name=Issue Type&cat=12`, setIssueTypeDataSource);
    load(`${base}/api/mthlyReport/proj/iss/rootCuz`, data => setIssueRootCauseDataSource(data.rootCuz));
    load(`${base}/api/mthlyReport/proj/iss/psList/${contrNo}`, data => {
      setParticipants(data);
      setParticipantsDataSource(new DataSource({
        store: { type: "array", data, key: "employeeNo" },
        group: "role",
      }));
    }, { params: { yearMonth } });
    return () => { active = false; };
  }, [contrNo, yearMonth]);

  const handleCustomItemCreate = (e) => {
    if (!e.text) {
      e.customItem = null;
      return;
    }
    const newItem = e.text;
    const dataSource = e.component.getDataSource();
    const isExistingItem = e.component
      .getDataSource()
      .items()
      .includes(newItem);
    if (isExistingItem) {
      // 이미 존재하는 데이터라면 customItem을 newItem으로 지정한다.
      e.customItem = newItem;
    } else {
      // 존재하지 않는 데이터라면 dataSource에 추가하고 customItem을 newItem으로 지정한다.
      e.customItem = dataSource
        .store()
        .insert(newItem)
        .then(() => dataSource.load())
        .then(() => newItem)
        .catch((error) => {
          throw error;
        });
    }
  };

  const handleDeleteRow = (e) => {
    const data = e.row.data;
    if (!data.order) {
      // order가 없다면 deleteRow
      e.component.deleteRow(e.row.rowIndex);
    } else {
      // 있다면 dataSource에서 remove
      e.component
        .getDataSource()
        .store()
        .push([{ type: "remove", key: data.order }]);
    }
  };

  const handleInitNewRow = (e) => {
    e.data.rootCuz = null;
    e.data.ac = null;
    e.data.expResDate = null;
    e.data.actResDate = null;
  };

  const handleParticipantsExpr = (e) => {
    if (!e) return null;
    return `${e.name} (${e.employeeNo})`;
  };

  const validateGridValue = () => {
    const gridInstance = cuzNAcGridRef.current.instance;

    if (gridInstance.getVisibleRows().length < 1) {
      alert(`Root Cause & Action 테이블은 필수항목 입니다`, "Validation Error");
      return false;
    }

    const validationState =
      gridInstance.getController("validating")._validationState;
    const errorMessages = [];

    const visibleRowKeys = gridInstance.getVisibleRows().map((row) => row.key);

    validationState.forEach((row) => {
      if (visibleRowKeys.includes(row.key) && row.validationResults) {
        const cellValidation = Object.values(row.validationResults);
        cellValidation.forEach((c) => {
          if (c.status === "invalid") {
            const rule = c.brokenRule;
            const message =
              rule.type === "required"
                ? `${c.name}은 필수 항목입니다`
                : rule.message;
            errorMessages.push(message);
          }
        });
      }
    });

    if (errorMessages.length > 0) {
      alert(errorMessages.join("<br />"), "Validation Error");
      return false;
    }
    return true;
  };

  // 두 데이터 세트 간의 차이점을 찾아 반환합니다.
  const getDifferencesBetweenData = (originalData, updatedData) => {
    return Object.keys(updatedData).reduce((differences, key) => {
      if (originalData[key] !== updatedData[key]) {
        if (
          key === "assocdId" &&
          originalData[key] === null &&
          updatedData[key] === "NA"
        ) {
        } else {
          differences[key] = updatedData[key];
        }
      }
      return differences;
    }, {});
  };

  // issueFormRef의 데이터에 codeId와 대응되는 codeListId를 맵핑한다.
  const mapCodeListIdForSubmit = () => {
    const currFormData = issueFormRef.current.instance.option("formData");
    const newData = isNewRecord
      ? currFormData
      : getDifferencesBetweenData(formData, currFormData);

    // codeId와 대응되는 codeListId를 매핑
    const mappings = [
      {
        field: "statCodeId",
        dataSource: issueStatusDataSource,
        targetField: "statCodeListId",
      },
      {
        field: "gradCodeId",
        dataSource: issueGradeDataSource,
        targetField: "gradCodeListId",
      },
      {
        field: "typeCodeId",
        dataSource: issueTypeDataSource,
        targetField: "typeCodeListId",
      },
    ];

    // statCodeId, gradCodeId, typeCodeId가 newData에 포함된 경우, newData에 codeListId를 추가합니다.
    mappings.forEach(({ field, dataSource, targetField }) => {
      if (newData[field]) {
        newData[targetField] = dataSource.codeListId;
      }
    });

    return newData;
  };

  const resetFormAndGrid = () => {
    issueFormRef.current.instance.resetValues();
    cuzNAcGridRef.current.instance.cancelEditData();
    cuzNAcGridRef.current.instance.resetOption("dataSource");
  };

  const handleSubmitIssue = async (e) => {
    const isValid = issueFormRef.current.instance.validate().isValid;
    if (!isValid || !validateGridValue()) {
      return;
    }

    const formDataForSubmit = mapCodeListIdForSubmit();
    const cuzNAcGridData = cuzNAcGridRef.current.instance
      .getVisibleRows()
      .map((el) => el.data);
    const label = e.element.ariaLabel;
    switch (label) {
      case "계속 추가":
      case "추가":
        await issueDataGridRef.current.instance
          .getDataSource()
          .store()
          // intShr: "Y", assocdId: "NA"와 같이 객체에 미리 값을 넣어주는 이유는 특정 필드에 대해 기본값을 설정하기 위함.
          .insert({
            formData: { intShr: "Y", assocdId: "NA", ...formDataForSubmit },
            cuzNAcArr: cuzNAcGridData,
          })
          .then((data) => {
            if (label === "계속 추가") {
              resetFormAndGrid();
            }
            if (label === "추가") {
              handleHidePopup();
            }
            toastMsg(`${data.id}가 정상적으로 등록되었습니다`, true);
            issueDataGridRef.current.instance.refresh(); // issueDataGrid 업데이트
            projectListRef.current.instance.refresh(); // 이슈 개수 업데이트
          });
        break;

      case "수정":
        // 기존 formData.cuzNAcArr와 현재 cuzNAcGridRef 데이터를 비교하여 수정 또는 삭제된 데이터를 찾는다.
        const modifiedOrDeletedData = formData.cuzNAcArr.reduce(
          (result, originalItem) => {
            const { order, ...originalData } = originalItem;
            const currentItem = cuzNAcGridData.find(
              (item) => item.order === order
            );
            if (currentItem) {
              const { order: _, ...currentData } = currentItem;
              result.push({
                order,
                ...getDifferencesBetweenData(originalData, currentData),
              });
            } else {
              result.push({ order, status: "deleted" });
            }
            return result;
          },
          []
        );
        // cuzNAcGrid에 새롭게 추가된 아이템
        const newlyAddedItems = cuzNAcGridData.filter(
          (el) => typeof el.order !== "number"
        );
        await issueDataGridRef.current.instance
          .getDataSource()
          .store()
          .update(id, {
            formData: formDataForSubmit,
            cuzNAcArr: modifiedOrDeletedData,
            newCuzNAc: newlyAddedItems,
          })
          .then(() => {
            toastMsg(`${id}가 정상적으로 수정되었습니다`, true);
            issueDataGridRef.current.instance.refresh(); // issueDataGrid 업데이트
            handleHidePopup();
          });
        break;

      default:
        console.error("알 수 없는 액션", label);
        return;
    }
  };

  const renderCustomizeItem = (e) => {
    const dataField = e.dataField;
    const labelLocation = e.editorOptions.labelLocation;
    const isRequired = e.editorOptions.labelMark.trim() === "*";
    return (
      <LabelEditorWrapper
        style={{
          flexDirection: labelLocation === "top" ? "column" : "row",
          alignItems: labelLocation === "top" ? "flex-start" : "center",
        }}
      >
        <div>
          <label
            className="dx-field-item-label dx-field-item-label-location-left"
            htmlFor={e.editorOptions.inputAttr.id}
          >
            {e.editorOptions.label}
            <span className="dx-field-item-required-mark">
              {e.editorOptions.labelMark}
            </span>
          </label>
          <FontAwesomeIcon icon={faCircleInfo} id={`${e.dataField}Icon`} />
          <Popover
            hideEvent={"mouseleave"}
            showEvent={"mouseenter"}
            target={`#${e.dataField}Icon`}
          >
            {issueFormData[e.dataField]}
          </Popover>
        </div>
        <div>
          {e.editorType === "dxSelectBox" ? (
            <SelectBox
              {...e.editorOptions}
              // defaultValue={formData?.[dataField]}
              // onInitialized={() => issueFormRef.current.instance.updateData(dataField, formData?.[dataField])}
              onValueChanged={(e) =>
                issueFormRef.current.instance.updateData(dataField, e.value)
              }
            >
              {isRequired && (
                <Validator validationGroup="issueFormValidation">
                  <RequiredRule message="" />
                </Validator>
              )}
            </SelectBox>
          ) : e.editorType === "dxRadioGroup" ? (
            <RadioGroup
              {...e.editorOptions}
              defaultValue={formData?.[dataField] || "Y"}
              // onInitialized={() => issueFormRef.current.instance.updateData(dataField, formData?.[dataField])}
              onValueChanged={(e) =>
                issueFormRef.current.instance.updateData(dataField, e.value)
              }
            />
          ) : e.editorType === "dxDateBox" ? (
            <DateBox
              {...e.editorOptions}
              defaultValue={formData?.[dataField]}
              dateSerializationFormat="yyyy-MM-dd"
              // onInitialized={() => issueFormRef.current.instance.updateData(dataField, formData?.[dataField])}
              onValueChanged={(e) =>
                issueFormRef.current.instance.updateData(dataField, e.value)
              }
              useMaskBehavior={true}
            >
              {isRequired && (
                <Validator validationGroup="issueFormValidation">
                  <RequiredRule message="" />
                </Validator>
              )}
            </DateBox>
          ) : e.editorType === "dxTextArea" ? (
            <TextArea
              {...e.editorOptions}
              defaultValue={formData?.[dataField]}
              // onInitialized={() => issueFormRef.current.instance.updateData(dataField, formData?.[dataField])}
              onValueChanged={(e) =>
                issueFormRef.current.instance.updateData(dataField, e.value)
              }
            >
              <Validator validationGroup="issueFormValidation">
                {isRequired && <RequiredRule message="" />}
              </Validator>
            </TextArea>
          ) : (
            ""
          )}
        </div>
      </LabelEditorWrapper>
    );
  };

  const renderDropDown = (drop) => {
    return (
      <div style={{ display: "flex", alignItems: "center" }}>
        <div
          style={{
            fontWeight: "bold",
            marginRight: "20px",
            display: "inline-block",
          }}
        >
          {drop.name}
        </div>
        <div
          style={{
            height: "auto",
            whiteSpace: "normal",
            overflowWrap: "break-word",
          }}
        >
          {drop.descr}
        </div>
      </div>
    );
  };

  const renderEditTextArea = (cell) => {
    return (
      <TextArea
        autoResizeEnabled={true}
        defaultValue={cell.value}
        inputAttr={{ style: "line-height: 1.5em; padding: 0.5em;" }}
        minHeight={20}
        onInitialized={(e) => {
          // * textarea auto-resizing
          setTimeout(() => {
            e.component.repaint();
          }, 0);
        }}
        onKeyDown={(e) => {
          // * new line with "Enter" key. update Cell size.
          if (e.event.key === "Enter") {
            e.event.stopPropagation();
          }
          cell.component.updateDimensions();
        }}
        onValueChanged={(e) => {
          cell.setValue(e.value);
        }}
      />
    );
  };

  const renderLabelPopover = (e) => {
    const caption = e.column.caption;
    const dataField = e.column.dataField;
    return (
      <>
        <span style={{ paddingRight: "8px" }}>{caption}</span>
        <FontAwesomeIcon icon={faCircleInfo} id={`${dataField}Icon`} />
        <Popover
          hideEvent={"mouseleave"}
          showEvent={"mouseenter"}
          target={`#${dataField}Icon`}
        >
          {issueFormData[dataField]}
        </Popover>
      </>
    );
  };

  return (
    assocdIdDataSource &&
    issueGradeDataSource &&
    issueRootCauseDataSource &&
    issueStatusDataSource &&
    issueTypeDataSource &&
    participantsDataSource && (
      <Popup
        hideOnOutsideClick={true}
        height={1000}
        maxHeight={"90vh"}
        maxWidth={"90vw"}
        onHiding={handleHidePopup}
        title={isNewRecord ? "Create Issue" : "Edit Issue"}
        visible={isPopupVisible}
        width={1200}
      >
        <ScrollView height="100%" width="100%">
          <FormWrapper>
            <Form
              height={"100%"}
              labelLocation="left"
              labelMode="outside"
              ref={issueFormRef}
              requiredMark="*"
              requiredMessage=""
              validationGroup="issueFormValidation"
            >
              <GroupItem colCount={2} colSpan={2}>
                <Item
                  dataField="statCodeId"
                  editorOptions={{
                    dataSource: issueStatusDataSource?.code,
                    defaultValue: formData?.statCodeId || null,
                    displayExpr: "name",
                    itemRender: renderDropDown,
                    searchEnabled: true,
                    valueExpr: "codeId",
                  }}
                  editorType="dxSelectBox"
                  isRequired={true}
                  label={{ text: "Status", visible: false }}
                  render={renderCustomizeItem}
                />
                <Item
                  dataField="intShr"
                  editorOptions={{
                    dataSource: [
                      { text: "Yes", value: "Y" },
                      { text: "No", value: "N" },
                    ],
                    displayExpr: "text",
                    layout: "horizontal",
                    valueExpr: "value",
                  }}
                  editorType="dxRadioGroup"
                  isRequired={true}
                  label={{ text: "Internal Share", visible: false }}
                  render={renderCustomizeItem}
                />
                <Item
                  dataField="gradCodeId"
                  editorOptions={{
                    dataSource: issueGradeDataSource?.code,
                    defaultValue: formData?.gradCodeId || null,
                    displayExpr: "name",
                    itemRender: renderDropDown,
                    searchEnabled: true,
                    valueExpr: "codeId",
                  }}
                  editorType="dxSelectBox"
                  isRequired={true}
                  label={{ text: "Grade", visible: false }}
                  render={renderCustomizeItem}
                />
                <Item
                  dataField="assocdId"
                  editorOptions={{
                    dataSource: ["NA", ...assocdIdDataSource],
                    defaultValue:
                      isNewRecord || !formData.assocdId
                        ? "NA"
                        : formData.assocdId,
                    searchEnabled: true,
                  }}
                  editorType="dxSelectBox"
                  isRequired={true}
                  label={{ text: "Associated Id", visible: false }}
                  render={renderCustomizeItem}
                />
                <Item
                  dataField="typeCodeId"
                  editorOptions={{
                    dataSource: issueTypeDataSource?.code,
                    defaultValue: formData?.typeCodeId || null,
                    displayExpr: "name",
                    itemRender: renderDropDown,
                    searchEnabled: true,
                    valueExpr: "codeId",
                  }}
                  editorType="dxSelectBox"
                  isRequired={true}
                  label={{ text: "Type", visible: false }}
                  render={renderCustomizeItem}
                />
                <Item
                  dataField="oEmployeeNo"
                  editorOptions={{
                    dataSource: participantsDataSource,
                    defaultValue: formData?.oEmployeeNo || null,
                    displayExpr: handleParticipantsExpr,
                    grouped: true,
                    searchEnabled: true,
                    valueExpr: "employeeNo",
                  }}
                  editorType="dxSelectBox"
                  isRequired={true}
                  label={{ text: "Owner", visible: false }}
                  render={renderCustomizeItem}
                />
                <Item
                  dataField="iDate"
                  editorOptions={{
                    dateOutOfRangeMessage: `${year}년 ${month}월에 발생한 이슈여야 합니다`,
                    displayFormat: "yyyy-MM-dd",
                    invalidDateMessage: "YYYY-MM-DD 형식이어야 합니다",
                    max: new Date(year, month, 0),
                    min: new Date(year, month - 1, 1),
                  }}
                  editorType="dxDateBox"
                  isRequired={true}
                  label={{ text: "Date Identified", visible: false }}
                  render={renderCustomizeItem}
                />
                <Item
                  dataField="eBEmployeeNo"
                  editorOptions={{
                    dataSource: enteredByDataSource,
                    defaultValue: formData?.eBEmployeeNo || null,
                    displayExpr: handleParticipantsExpr,
                    grouped: true,
                    searchEnabled: true,
                    valueExpr: "employeeNo",
                  }}
                  editorType="dxSelectBox"
                  isRequired={true}
                  label={{ text: "Entered By", visible: false }}
                  render={renderCustomizeItem}
                />
                <Item
                  dataField="issDescr"
                  editorOptions={{
                    height: 160,
                    labelLocation: "top",
                    maxLength: 4000,
                  }}
                  editorType="dxTextArea"
                  isRequired={true}
                  label={{
                    location: "top",
                    text: "Issue Description (max length = 4000)",
                    visible: false,
                  }}
                  render={renderCustomizeItem}
                />
                <Item
                  dataField="iSum"
                  editorOptions={{
                    height: 160,
                    labelLocation: "top",
                    maxLength: 4000,
                  }}
                  editorType="dxTextArea"
                  isRequired={true}
                  label={{
                    location: "top",
                    text: "Impact Summary (max length = 4000)",
                    visible: false,
                  }}
                  render={renderCustomizeItem}
                />
                <Item
                  dataField="cuzNAcGridRef"
                  colSpan={2}
                  label={{ visible: false }}
                >
                  <DataGrid
                    allowColumnResizing={true}
                    columnResizingMode={"nextColumn"}
                    dataSource={[...(formData?.cuzNAcArr || [])]}
                    height={"100%"}
                    id="cuzNAcGrid"
                    keyExpr="order"
                    onCellPrepared={handleCellStyle}
                    onInitNewRow={handleInitNewRow}
                    showBorders={true}
                    showColumnLines={true}
                    ref={cuzNAcGridRef}
                    width={"100%"}
                    wordWrapEnabled={true}
                  >
                    <Editing
                      allowAdding={true}
                      allowDeleting={true}
                      allowUpdating={true}
                      mode="batch"
                      newRowPosition="last"
                    />
                    <Toolbar>
                      <DataGridItem name="addRowButton" location="after" />
                    </Toolbar>
                    <Column
                      dataField="rootCuz"
                      alignment="center"
                      caption="Root Cause"
                      editorOptions={{
                        acceptCustomValue: true,
                        onCustomItemCreating: handleCustomItemCreate,
                        searchEnabled: true,
                        wrapItemText: true,
                      }}
                      headerCellRender={renderLabelPopover}
                      showEditorAlways={true}
                      width={200}
                    >
                      <Lookup dataSource={issueRootCauseDataSource} />
                      <GridRequiredRule message="" />
                    </Column>
                    <Column
                      dataField="ac"
                      alignment="center"
                      caption="Action"
                      editCellRender={renderEditTextArea}
                      headerCellRender={renderLabelPopover}
                      showEditorAlways={true}
                    >
                      <GridRequiredRule message="" />
                    </Column>
                    <Column
                      dataField="expResDate"
                      alignment="center"
                      caption="Expected Resolution Date"
                      dataType="date"
                      editorOptions={{
                        dateSerializationFormat: "yyyy-MM-dd",
                        defaultValue: formData?.expResDate,
                        useMaskBehavior: true,
                      }}
                      format="yyyy-MM-dd"
                      headerCellRender={renderLabelPopover}
                      showEditorAlways={true}
                      width={120}
                    >
                      <GridRequiredRule message="" />
                    </Column>
                    <Column
                      dataField="actResDate"
                      alignment="center"
                      caption="Actual Resolution Date"
                      dataType="date"
                      editorOptions={{
                        dateSerializationFormat: "yyyy-MM-dd",
                        defaultValue: formData?.actResDate,
                        displayFormat: "yyyy-MM-dd",
                        useMaskBehavior: true,
                      }}
                      format="yyyy-MM-dd"
                      headerCellRender={renderLabelPopover}
                      showEditorAlways={true}
                      width={120}
                    />
                    <Column type="buttons">
                      <Button name="delete" onClick={handleDeleteRow} />
                    </Column>
                  </DataGrid>
                </Item>
                <Item
                  dataField="fResR"
                  colSpan={2}
                  editorOptions={{ height: 160, labelLocation: "top" }}
                  editorType="dxTextArea"
                  label={{
                    location: "top",
                    text: "Final Resolution & Rationale",
                    visible: false,
                  }}
                  render={renderCustomizeItem}
                />
              </GroupItem>
            </Form>
          </FormWrapper>
        </ScrollView>
        <ToolbarItem
          location="after"
          toolbar="bottom"
          options={{ onClick: handleSubmitIssue, text: "계속 추가" }}
          visible={isNewRecord}
          widget="dxButton"
        />
        <ToolbarItem
          location="after"
          toolbar="bottom"
          options={{
            onClick: handleSubmitIssue,
            text: isNewRecord ? "추가" : "수정",
          }}
          widget="dxButton"
        />
        <ToolbarItem
          location="after"
          toolbar="bottom"
          options={{ onClick: handleHidePopup, text: "취소" }}
          widget="dxButton"
        />
      </Popup>
    )
  );
};

export default IssueFormPopup;
